import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Langue } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { ReponseDocumentSigne } from './documentsGalerie.js';
export interface DossierOeuvresPresente {
  id: string;
  titre: string;
  contactId: string | null;
  oeuvres: string[];
  message: string;
  langue: Langue;
  avecPrix: boolean;
  avecProvenance: boolean;
  archiveLe: string | null;
  creeLe: string;
  modifieLe: string;
}
export type CorpsDossierOeuvres = Pick<DossierOeuvresPresente, 'titre' | 'oeuvres'> &
  Partial<
    Pick<DossierOeuvresPresente, 'contactId' | 'message' | 'langue' | 'avecPrix' | 'avecProvenance'>
  >;
export interface FiltresDossiersOeuvres {
  contactId?: string;
  archives?: boolean;
}
const racine = (g: string) => `/g/${encodeURIComponent(g)}/dossiers-oeuvres`;
export const dossiersOeuvresApi = {
  lister: (g: string, f: FiltresDossiersOeuvres = {}, a?: AppelApi) => {
    const q = new URLSearchParams();
    if (f.contactId) q.set('contactId', f.contactId);
    if (f.archives !== undefined) q.set('archives', String(f.archives));
    return api.get<{ dossiers: DossierOeuvresPresente[] }>(`${racine(g)}?${q}`, {
      galerie: g,
      ...a,
    });
  },
  lire: (g: string, id: string, a?: AppelApi) =>
    api.get<{ dossier: DossierOeuvresPresente }>(`${racine(g)}/${encodeURIComponent(id)}`, {
      galerie: g,
      ...a,
    }),
  creer: (g: string, c: CorpsDossierOeuvres, a?: AppelApi) =>
    api.post<{ dossier: DossierOeuvresPresente }>(racine(g), c, { galerie: g, ...a }),
  modifier: (g: string, id: string, c: Partial<CorpsDossierOeuvres>, a?: AppelApi) =>
    api.patch<{ dossier: DossierOeuvresPresente }>(`${racine(g)}/${encodeURIComponent(id)}`, c, {
      galerie: g,
      ...a,
    }),
  archiver: (g: string, id: string, a?: AppelApi) =>
    api.post(`${racine(g)}/${encodeURIComponent(id)}/archiver`, {}, { galerie: g, ...a }),
  pdf: (g: string, id: string, a?: AppelApi) =>
    api.get<ReponseDocumentSigne>(`${racine(g)}/${encodeURIComponent(id)}/pdf`, {
      galerie: g,
      ...a,
    }),
};
export function useDossiersOeuvres(g: string, f: FiltresDossiersOeuvres = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'dossiers-oeuvres', f),
    queryFn: (c) => dossiersOeuvresApi.lister(g, f, appelDeRequete(c)),
  });
}
function useGeste<T, R>(g: string, fn: (v: T, a: AppelApi) => Promise<R>) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: fn,
      onSuccess: () =>
        void client.invalidateQueries({ queryKey: cleMetier(g, 'dossiers-oeuvres') }),
    }),
  );
}
export const useCreerDossierOeuvres = (g: string) =>
  useGeste(g, (c: CorpsDossierOeuvres, a) => dossiersOeuvresApi.creer(g, c, a));
export const useOuvrirDossierPdf = (g: string) =>
  useGeste(g, (id: string, a) => dossiersOeuvresApi.pdf(g, id, a));
export const useArchiverDossierOeuvres = (g: string) =>
  useGeste(g, (id: string, a) => dossiersOeuvresApi.archiver(g, id, a));
